import { useEffect, useState } from "react";
import type { HoursProfile, OrderingState, StoreCurrentResponse } from "@toema/shared-types";
import Hero from "./components/Hero.js";
import Menu from "./components/Menu.js";
import Ticket from "./components/Ticket.js";
import OrderForm from "./components/OrderForm.js";
import StickyBar from "./components/StickyBar.js";
import Footer from "./components/Footer.js";
import Closed from "./components/Closed.js";
import { fetchMenu, fetchStore } from "./api.js";
import { groupMenu } from "./lib/menu.js";
import { shopFrom } from "./lib/shop.js";
import type { ShopIdentity } from "./lib/shop.js";
import type { DisplayGroup } from "./lib/menu.js";
import { useCart } from "./lib/cart.js";
import "./App.css";

interface StoreState {
  shop: ShopIdentity;
  blocked: boolean;
  hours: HoursProfile;
  ordering: OrderingState;
}

const ORDERING_RECHECK_MS = 60_000;

function storeStateFrom(loaded: StoreCurrentResponse): StoreState {
  return {
    shop: shopFrom(loaded),
    blocked: loaded.status === "blocked",
    hours: {
      openingHours: loaded.profile.openingHours,
      timezone: loaded.profile.timezone,
      hourExceptions: loaded.profile.hourExceptions,
    },
    ordering: loaded.ordering,
  };
}

type MenuState =
  | { kind: "loading" }
  | { kind: "ready"; groups: readonly DisplayGroup[] }
  | { kind: "failed"; message: string };

export default function App() {
  const cart = useCart();
  const [menu, setMenu] = useState<MenuState>({ kind: "loading" });
  const [store, setStore] = useState<StoreState | null>(null);
  const [ticketNumber, setTicketNumber] = useState<number | null>(null);
  const [storeRecheck, setStoreRecheck] = useState(0);
  const paused = store?.ordering.accepting === false;

  useEffect(() => {
    const controller = new AbortController();

    fetchStore(controller.signal).then(
      (loaded) => setStore(storeStateFrom(loaded)),
      () => {
        /* The menu is what the page is for; a store that cannot be read leaves
           the header and footer empty rather than taking the page down. */
      },
    );

    fetchMenu(controller.signal).then(
      (loaded) => setMenu({ kind: "ready", groups: groupMenu(loaded) }),
      (error: unknown) => {
        // A StrictMode remount aborts the first request on purpose; that is
        // not a failure worth showing anybody.
        if (controller.signal.aborted) return;
        setMenu({
          kind: "failed",
          message:
            error instanceof Error
              ? error.message
              : "The menu could not be loaded.",
        });
      },
    );

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (storeRecheck === 0) return;
    const controller = new AbortController();
    fetchStore(controller.signal).then(
      (loaded) => setStore(storeStateFrom(loaded)),
      () => undefined,
    );
    return () => controller.abort();
  }, [storeRecheck]);

  useEffect(() => {
    if (!paused) return;
    const timer = setInterval(() => setStoreRecheck((n) => n + 1), ORDERING_RECHECK_MS);
    return () => clearInterval(timer);
  }, [paused]);

  useEffect(() => {
    if (store === null) return;
    document.title = `${store.shop.name} — order online`;
  }, [store]);

  if (store?.blocked) return <Closed shop={store.shop} />;

  return (
    <>
      {store !== null && (
        <Hero shop={store.shop} profile={store.hours} ordering={store.ordering} />
      )}

      <main className="wrap layout">
        <div className="layout__menu">
          {menu.kind === "loading" && (
            <p className="layout__note" role="status">
              Loading the menu…
            </p>
          )}
          {menu.kind === "failed" && (
            <p className="layout__note layout__note--bad" role="alert">
              {menu.message}
            </p>
          )}
          {menu.kind === "ready" && <Menu groups={menu.groups} cart={cart} paused={paused} />}
        </div>

        <aside className="layout__order" aria-label="Your order">
          <Ticket cart={cart} ticketNumber={ticketNumber} shopName={store?.shop.name ?? ""} />
          <OrderForm
            cart={cart}
            ordering={store?.ordering ?? null}
            timezone={store?.hours.timezone ?? null}
            onPlaced={setTicketNumber}
            onRefused={() => setStoreRecheck((n) => n + 1)}
          />
        </aside>
      </main>

      <StickyBar count={cart.count} totalCents={cart.totalCents} />
      {store !== null && (
        <Footer shop={store.shop} profile={store.hours} />
      )}
    </>
  );
}
